import common from '../common';
import utils, {translate, Text} from '../utils';
import {GlowFilter} from 'pixi-filters';

export default class Command extends PIXI.Container {
	/**
	 * @param texture
	 */
	constructor(texture) {
		super();

		this.grabbable = true;
		this.grabbing = false;
		this.dragData = null;
		this.moved = false;

		this.type = Command.TYPE_STATIC;
		this.occupied = true;
		this.locked = false;
		this.canChangeVariable = true;
		this.template = false;
		this.variable = -1;
		this.variableActive = common.levelSettings.abilities.includes('variables');
		this.defaultVariable = 1;
		this.glowFilter = null;

		this.attached = false;
		this.variableLabel = null;
		this.commandLabel = null;

		this._texture = texture;
		this._offTexture = P3.assets.texture(texture);

		this.placed = false;
		this._attachedRepeat = null;
		this._slotPosition = new PIXI.Point(0, 0);

		this.signals = {};
		this.signals.pickup = new P3.utils.MiniSignal();
		this.signals.touch = new P3.utils.MiniSignal();
		this.signals.move = new P3.utils.MiniSignal();
		this.signals.release = new P3.utils.MiniSignal();
		this.signals.executionComplete = new P3.utils.MiniSignal();
		this.signals.destroy = new P3.utils.MiniSignal();

		this.player = common.player;

		this._dragPoint = new PIXI.Point(0, 0);
		this.dispatched = false;

		this.repeatAlignTop = 48;
		this.repeatAlignBottom = 15;

		this.yPosition = -(common.commandTopBottomOffset >> 1);

		this.ui = this.initUI();

		this.initEvents();
	}

	init(label, icon) {
		// glow filter
		this.glowFilter = new GlowFilter(10, 4, 0, 0xFFFFFF, 0.5);

		// icon
		this.commandIcon = new PIXI.Sprite(P3.assets.texture(icon));
		this.commandIcon.anchor.set(0.5);
		this.commandIcon.x = -(common.commandDimensions.width >> 1) + (this.commandIcon.texture.width >> 1) + common.commandSpacing;
		this.addChild(this.commandIcon);

		// label
		this.commandLabel = Text(translate(label), {font: `${common.commandFontSize}px Ahkio Thin`, align: 'center'});
		this.commandLabel.tint = 0x363636;
		this.commandLabel.anchor.y = 0.5;
		this.commandLabel.x = this.commandIcon.x + (this.commandIcon.width >> 1) + common.commandSpacing;
		this.commandLabel.y = this.yPosition - common.commandFontOffset;

		this.addChild(this.commandLabel);

		// variable background
		this.variableBackground = new PIXI.Sprite(P3.assets.texture('ui_function_number'));
		this.variableBackground.anchor.set(0.5);
		this.variableBackground.x = (common.commandDimensions.width >> 1) - (this.variableBackground.width >> 1) - common.commandSpacing;
		this.variableBackground.y = this.yPosition;
		this.addChild(this.variableBackground);

		// variable label
		this.variableLabel = Text('', {font: `${common.commandFontSize + 10}px Ahkio Thin`, align: 'center'});
		this.variableLabel.tint = 0x363636;
		this.variableLabel.anchor.set(0.5);
		this.variableLabel.x = this.variableBackground.x;
		this.variableLabel.y = this.yPosition - common.commandFontOffset;

		this.addChild(this.variableLabel);
	}

	initUI() {
		if (['ui_function_start', 'ui_function_stop'].indexOf(this._texture) !== -1) {
			this.bg = new PIXI.Sprite(this._offTexture);
		} else {
			this.bg = new PIXI.mesh.NineSlicePlane(this._offTexture, 40, 18, 10, 18);

			this.bg.width = common.commandDimensions.width;
			this.bg.height = common.commandDimensions.height;
		}

		this.bg.x = -(this.bg.width >> 1);
		this.bg.y = -(this.bg.height >> 1);

		this.addChild(this.bg);

		return this.bg;
	}

	switchTexture(state) {
		this.ui.filters = state ? [this.glowFilter] : null;
	}

	darken(value) {
		this.bg.tint = value ? 0xc0c0c0 : 0xffffff;
	}

	initEvents() {
		this.ui.interactive = true;

		this.ui.on('pointerdown', (e) => {
			this.onPointerDown(e);
		}, this);

		this.ui.on('pointerup', (e) => {
			this.onPointerUp(e);
		}, this);

		this.ui.on('pointerupoutside', (e) => {
			this.onPointerUp(e);
		}, this);

		this.ui.on('pointermove', (e) => {
			this.onPointerMove(e);
		});
	}

	onPointerDown(e) {
		if (this.locked) {
			return;
		}

		this.signals.touch.dispatch(this, e);

		if (this.template) {
			this.dispatched = true;
			this.signals.pickup.dispatch(this, e);
		}

		if (!this.grabbable) {
			return;
		}

		this.grabbing = true;
		this.dragData = e.data;

		this._dragPoint.copy(e.data.global);

		if (this.parent) {
			const parent = this.parent;

			// sets the command to top
			parent.removeChild(this);
			parent.addChild(this);
		}
	}

	onPointerUp() {
		if (!this.grabbable || !this.grabbing || this.locked) {
			return;
		}

		this.grabbing = false;
		this.moved = false;
		this.dragData = null;
		this._dragPoint.set(0, 0);
		this._distance = 0;

		if (!this.dispatched) {
			return;
		}

		this.dispatched = false;
		this.signals.release.dispatch(this);
	}

	onPointerMove(e) {
		if (!this.grabbing || this.locked) {
			return;
		}

		this.moved = true;

		const d = utils.getDistance(e.data.global, this._dragPoint);

		if (d > 5 || this.dispatched) {
			if (!this.dispatched && !this.template) {
				this.dispatched = true;
				this.signals.pickup.dispatch(this, e);
			}

			const newpos = this.dragData.getLocalPosition(this.parent);

			if (!this.placed) {
				this.x = newpos.x;
			}

			this.y = newpos.y;

			this.signals.move.dispatch(this);
		}
	}

	destroy() {
		super.destroy();
	}

	get slotPosition() {
		return this._slotPosition;
	}

	set slotPosition(v) {
		this._slotPosition = v;
	}

	get attachedRepeat() {
		return this._attachedRepeat;
	}

	set attachedRepeat(v) {
		this._attachedRepeat = v;
	}
}

Command.TYPE_STATIC 	= 'static';
Command.TYPE_WALK 		= 'walk';
Command.TYPE_LOOP 		= 'loop';
Command.TYPE_JUMP 		= 'jump';
Command.TYPE_TURN		= 'turn';
Command.TYPE_TRANSFORM 	= 'transform';
Command.TYPE_EMPTY		= 'empty';

Command.TYPE_VARIABLE	= 'variable';
Command.TYPE_EMPTY_VAR	= 'empty_variable';
Command.TYPE_TURN_VAR	= 'turn_variable';
